import { useTranslation } from 'next-i18next';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

import { NAMESPACE } from './ui';

interface TrialDialogProps {
  open: boolean;
  onClose: () => void;
  /** where the page moves the signup form while the dialog is open */
  bodyRef: RefObject<HTMLDivElement>;
}

export const supportsDialog = () => typeof window !== 'undefined' && typeof window.HTMLDialogElement === 'function' && typeof window.HTMLDialogElement.prototype.showModal === 'function';

// Every "Testar grátis" CTA on the page opens THIS dialog with the hero's own form inside it (the page
// moves the form's node here and back, so nothing the visitor typed is lost). Native <dialog>: focus is
// trapped by the browser, Esc closes it, and the backdrop click closes it too. Focus goes to the first
// field and returns to the control that opened it.
export function TrialDialog({ open, onClose, bodyRef }: TrialDialogProps) {
  const { t } = useTranslation(NAMESPACE);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previousFocus = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !supportsDialog()) return undefined;
    if (open && !dialog.open) {
      previousFocus.current = document.activeElement;
      dialog.showModal();
      const timer = window.setTimeout(() => dialog.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true }), 50);
      return () => window.clearTimeout(timer);
    }
    if (!open && dialog.open) dialog.close();
    return undefined;
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const handleClose = () => {
      onClose();
      const target = previousFocus.current;
      if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the click only closes on the backdrop itself (event.target === dialog); keyboard users have Esc and the close button
    <dialog
      ref={dialogRef}
      className='np-trial-dialog'
      aria-label={t('dialog.aria')}
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
    >
      <div className='np-trial-dialog__panel'>
        <button className='np-trial-dialog__close' type='button' aria-label={t('dialog.close')} onClick={() => dialogRef.current?.close()}>
          &times;
        </button>
        <div className='np-trial-dialog__body' ref={bodyRef} />
      </div>
    </dialog>
  );
}
